import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@components/ui/Modal/Modal';
import { Button } from '@components/ui/Button/Button';
import styles from './CreateScheduleModal.module.css';

export interface ICreateScheduleModalProps {
  open: boolean;
  onClose: () => void;
  /** Create the schedule with the (trimmed) name. */
  onCreate: (name: string) => void;
  /** A create request is in flight — disables the input + Create button. */
  creating: boolean;
  /**
   * Portal target. Omit for the default (document.body); the Scheduler passes its
   * fullscreen element so the modal stays visible while the page is fullscreen.
   */
  container?: HTMLElement | null;
}

/**
 * Name-a-new-schedule modal. A single text input (autofocused) + Cancel/Create.
 * Create is disabled while the trimmed name is empty or a create is in flight;
 * Enter submits. Built on the shared ui/Modal so it inherits the dialog chrome
 * and fullscreen-aware portalling.
 */
export function CreateScheduleModal({ open, onClose, onCreate, creating, container }: ICreateScheduleModalProps) {
  const { t } = useTranslation();
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [name, setName] = useState('');

  // Reset the field when the modal transitions to open — done during render (the
  // React-recommended "store info from previous renders" pattern) rather than in
  // an effect, so it doesn't trip the set-state-in-effect rule.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setName('');
  }

  // Focus the input after Base UI mounts the popup (a tick after `open`).
  useEffect(() => {
    if (!open) return;
    const handle = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(handle);
  }, [open]);

  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && !creating;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onCreate(trimmed);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title={t('scheduler.create.title')}
      container={container}
      footer={
        <>
          <span className={styles.spacer} />
          <Button variant="secondary" size="sm" onClick={onClose} disabled={creating}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={submit}
            disabled={!canSubmit}
            aria-busy={creating}
            data-testid="schedule-create-confirm"
          >
            {t('scheduler.create.confirm')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className={styles.form}>
        <label htmlFor={inputId} className={styles.label}>
          {t('scheduler.create.nameLabel')}
        </label>
        <input
          id={inputId}
          ref={inputRef}
          type="text"
          data-testid="schedule-create-name"
          className={styles.input}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('scheduler.create.namePlaceholder')}
          maxLength={200}
          disabled={creating}
        />
      </form>
    </Modal>
  );
}
